import React, { useState, useEffect, useCallback } from 'react';
import joi from 'joi';
import dayjs from 'dayjs';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Clear as ClearIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';
import { DatePicker, TimeField } from '@mui/x-date-pickers';
import { enableMapSet } from 'immer';
import { useImmer } from "use-immer";

import { useSession, useMisc } from 'store';
import { Page, FormLabel } from 'components';
import {
  callApi,
  getCrossDayDefaultOverflowPercent,
  getJobJump2InputValue,
  toJobJump2StorageValue,
  useGoBack,
  validate,
} from 'utils';
import { ScheduleEditor } from './components';
import { CUST_MARK, postbackTypes, jobAttrs, jobAttrFixedOverrun } from 'consts';

enableMapSet();

const schema = joi.object({
  org: joi.number().label('组织').required(),
  name: joi.string().label('任务名称').required(),
  ad_slot_name: joi.string().label('广告位').allow(''),
  start_date: joi.object().label('开始日期').invalid(null).required(),
  end_date: joi.object().label('结束日期').invalid(null).required(),
  ctr: joi.number().label('CTR').min(0).max(100).required(),
  daily_imps: joi.object({
    default: joi.number().label('默认日流量').integer().min(0).required(),
  }),
  dayparts: joi.object({
    default: joi.array().label('默认投放时段').items(joi.object()).optional(),
  }),
  overrun: joi.when('cross_day_schedule', {
    is: true,
    then: joi.number().label('溢出比例').min(0).required(),
    otherwise: joi.number().label('溢出比例').integer().min(0).required(),
  }),
  is_adx: joi.bool().label('来自 Adx'),
  adx_tagid: joi.string().label('Adx 标签ID').allow(''),
  //  .when('is_adx', { is: true, then: joi.pattern(/^[0-9a-f]{16}$/), otherwise: joi.allow('') }),
  dest_url: joi.string().label('落地页代码').allow('').regex(/^https?:/),
  trackers: joi.object(),
  traffic_curve: joi.string(),
  postback: joi.object(),
  fc_id: joi.number().integer(),
  fc_max: joi.number().integer().required(),
  landing: joi.number().integer().required(),
  device_filter: joi.string().allow(''),
  //ant_cfg: joi.string().allow(''),
  ant_cfg_cust: joi.any()
    .when('ant_cfg', {is: CUST_MARK, then: joi.string().pattern(/^[-0-9a-z]+\.yml$/), otherwise: joi.string().allow('')}),
  region_names: joi.string().allow(''),
  parent: joi.number().integer(),
  attrs: joi.number().integer(),
  extra: joi.string().allow(''),
  cross_day_schedule: joi.bool(),
  jump2: joi.string().label('目标二跳').allow('').pattern(/^\d+%?$/),
  bounceTarget: joi.string().label('目标跳出率').allow('').pattern(/^(?:100|[0-9]?\d)%?$/),
  cust_device: joi.string().allow(''),
  //cust_cookie: joi.alternatives().try(joi.number(), joi.allow('')).optional(),
  cust_cookie: joi.string().allow(''),
});

const isInvalidUrl = (s) => /[\s\n]/.test(s);

// set default key for daily_imps and dayparts
function normalizeDefaultKey(startDate, endDate, data) {
  if (Object.keys(data) <= 1)
    return data;

  const dateList = [];
  for (let day = startDate; !day.isAfter(endDate, 'day'); day = day.add(1, 'day')) {
    dateList.push(day.format('YYYYMMDD'));
  }

  const getDayVal = (day) => day in data ? data[day] : data.default;

  // count of each data value
  const cnt = {};
  for (let day of dateList) {
    const val = getDayVal(day);
    cnt[val] ??= 0;
    cnt[val] += 1;
  }

  const freqVal = Object.keys(cnt).reduce((p, c) => cnt[c] > cnt[p] ? c : p);
  const res = {default: freqVal};
  for (let day of dateList) {
    const val = getDayVal(day);
    if (''+val !== freqVal) res[day] = val;
  }
  return res;
}

const JobForm = () => {
  const session = useSession((state) => state.session);
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();
  const goBack = useGoBack('/jobs');
  const misc = useMisc((state) => state.data);
  const [showDayparts, setShowDayparts] = useState(true);
  const copyJob = searchParams.get('copy');

  const isAdmin = session.role === 'admin';

  const [formState, setFormState] = useImmer({
    values: {
      org: 1,
      name: '',
      ad_slot_name: '',
      start_date: null,
      end_date: null,
      ctr: 0,
      daily_imps: {default: 0},
      dayparts: {},  // {default: []}
      overrun: 20,
      is_adx: false,
      adx_tagid: '',
      dest_url: '',
      trackers: {imp: [''], clk: ['']},
      traffic_curve: 'default',
      postback: new Set(),
      fc_id: 0,
      fc_max: 1,
      landing: 100,
      device_filter: 'ios_30',
      ant_cfg: 'auto.yml',
      ant_cfg_cust: '',
      region_names: '',
      regions: null,
      parent: 0,
      attrs: 0,
      extra: '',
      cross_day_schedule: false,
      cross_day_display_dayparts: '',
      cross_day_role: '',
      cross_day_source_job_id: '',
      jump2: '',
      bounceTarget: '',
      cust_device: '',
      cust_cookie: '',
    },
    touched: {},
    errors: null,
  });
  const [saving, setSaving] = useState(false);
  const [trafficCurves, setTrafficCurves] = useState({});

  // for edit or copy job
  useEffect(() => {
    const getId = jobId || copyJob;
    if (!getId)
      return;
    callApi(`/jobs/${getId}`).then(job => {
      job.start_date = dayjs(job.start_date);
      job.end_date = dayjs(job.end_date);
      let daily_imps;
      if (copyJob) {
        job.fc_id = job.fc_id || copyJob;
        job.parent = job.parent || copyJob;
        job.ad_slot_name = '';
        // do not copy these fields
        daily_imps = {default: 0};
      } else {
        daily_imps = job.daily_imps;
        // the imps of past days are readonly when edit job, so we set default imps to 0 to prevent
        // setting imps of past days an unexpected, unchangable value if we expand end_date
        if (daily_imps.default > 0) {
          for (let day = job.start_date; !day.isAfter(job.end_date, 'day'); day = day.add(1, 'day')) {
            const d = day.format('YYYYMMDD');
            if (!(d in daily_imps)) daily_imps[d] = daily_imps.default;
          }
          daily_imps.default = 0;
        }
      }

      const dayparts = {};
      Object.entries(job.dayparts || {}).forEach(([day, dps]) =>
        dayparts[day] = dps.map(t => dayjs('2022-02-22 '+t))
      );
      // set default if has other custom dayparts
      if (!dayparts.default && Object.keys(dayparts).length > 0) {
        dayparts.default = [dayjs('2022-02-22 00:00'), dayjs('2022-02-22 23:59')];
      }
      // disallow change default dayparts if editing job with dayparts set
      if (jobId && Object.keys(dayparts).length > 0) {
        setShowDayparts(false);
      }

      const is_adx = job.dest_url.startsWith('adx:');

      let postback;
      if (job.postback)
        postback = new Set(job.postback === 'all' ? Object.keys(postbackTypes) : job.postback.split(','));
      else
        postback = new Set();

      delete job.suspend;
      if (!jobId) {
        delete job.id;
      }
      if (misc['ant_cfg'][job.ant_cfg] === undefined) {
        job.ant_cfg_cust = job.ant_cfg;
        job.ant_cfg = CUST_MARK;
      }
      const cust = JSON.parse(job.customize || '{}');
      const crossDaySchedule = copyJob ? null : cust?.cross_day_schedule;
      if (copyJob) delete cust.cross_day_schedule;
      if (crossDaySchedule?.role === 'main') {
        job.overrun = getCrossDayDefaultOverflowPercent(crossDaySchedule.overflow_percent);
      }
      setFormState({
        values: {
          ...job,
          customize: Object.keys(cust).length > 0 ? JSON.stringify(cust) : '',
          daily_imps,
          dayparts,
          is_adx,
          adx_tagid: is_adx ? job.dest_url.slice(4) : '',
          dest_url: is_adx ? '' : job.dest_url,
          trackers: {
            imp: job.trackers?.imp || [''],
            clk: job.trackers?.clk || [''],
          },
          postback,
          extra: (job.extra || '').replaceAll('@@', '\n'),
          cross_day_schedule: crossDaySchedule?.role === 'main',
          cross_day_display_dayparts: crossDaySchedule?.display_dayparts || '',
          cross_day_role: crossDaySchedule?.role || '',
          cross_day_source_job_id: crossDaySchedule?.source_job_id || '',
          jump2: getJobJump2InputValue(job.jump2),
          bounceTarget: Number(cust?.bounce) > 0 ? '' + cust.bounce : '',
          cust_device: cust?.device || '',
          cust_cookie: cust?.cookie || '',
        },
        touched: {},
        errors: null,
      });
    }).catch(console.error);
  }, [jobId, copyJob, misc, setFormState]);

  const { values: vals, errors: errs, touched } = formState;
  const isEditing = !!jobId;

  const handleChange = useCallback((ev) => {
    const t = ev.target;
    setFormState(draft => {
      let touchField = t.name;
      if (t.name === 'postback') {
        draft.values.postback = new Set(t.checked ? Object.keys(postbackTypes) : []);
      } else if (t.name.startsWith('pb_type_')) {
        const type = t.name.slice(8);
        const set = draft.values.postback;
        if (t.checked)
          set.add(type);
        else
          set.delete(type);
        touchField = 'postback';
      } else if (t.name.startsWith('attr_')) {
        const val = t.name.slice(5) |0;
        if (t.checked)
          draft.values.attrs |= val;
        else
          draft.values.attrs &= ~val;
        touchField = 'attrs';
      } else if (t.name === 'cross_day_schedule') {
        draft.values.cross_day_schedule = t.checked;
        if (t.checked && !draft.values.dayparts.default) {
          draft.values.dayparts.default = [dayjs('2022-02-22 09:00'), dayjs('2022-02-22 09:00')];
        }
        touchField = 'cross_day_schedule';
      } else if (t.name === 'region_names') {
        draft.values[t.name] = t.value;
        draft.values['regions'] = null;
      } else {
        draft.values[t.name] = t.type === 'checkbox' ? t.checked : t.value;
      }
      draft.touched[touchField] = true;
    });
  }, [setFormState]);

  const changeDate = (name, val) => setFormState(draft => {
    draft.values[name] = val;
    draft.errors = validate(draft.values, schema);
  });
  const showEditDayparts = () => setFormState(draft => {
    draft.values.dayparts.default = [dayjs('2022-02-22 00:00'), dayjs('2022-02-22 23:59')];
  });
  const changeDefaultDayparts = (i, val) => setFormState(draft => {
    draft.values.dayparts.default[i] = val;
  });
  const clearDayparts = () => setFormState(draft => {
    draft.values.dayparts = {};
  });
  const changeSchedule = useCallback((name, key, value) => {
    //console.log('schedule:', name, key, value);
    if (name === 'daily_imps') value |= 0;
    setFormState(draft => { draft.values[name][key] = value });
  }, [setFormState]);

  const handleBlur = useCallback(() => {
    //console.log(vals, errors);
    setFormState(draft => { draft.errors = validate(draft.values, schema) });
  }, [setFormState]);

  const handleValidateRegions = useCallback((ev) => {
    let val = ev.target.value;
    if (val === '') return;
    callApi(`/misc/regions/${encodeURIComponent(val)}`).then(res => {
      if (res.error) {
        setFormState(draft => { draft.errors = Object.assign(draft.errors||{}, {'regions': res.error}) });
      } else {
        setFormState(draft => {
          draft.values['regions'] = res.codes;
          delete draft.errors?.regions;
          if (draft.errors && Object.keys(draft.errors).length === 0)
            draft.errors = null;
        });
      }
    });
  }, [setFormState]);

  const isOverflowJob = vals.cross_day_role === 'overflow';
  const disableSubmit = Boolean(errs) || saving;
  Boolean(errs) && console.log(errs);

  useEffect(() => {
    let active = true;
    if (!isAdmin) {
      setTrafficCurves(misc.traffic_curve || {});
      return () => { active = false; };
    }

    const org = Number(vals.org);
    if (!(org > 0)) {
      setTrafficCurves({});
      return () => { active = false; };
    }

    callApi(`/misc?org=${org}`)
      .then(res => active && setTrafficCurves(res?.traffic_curve || {}))
      .catch(console.error);
    return () => { active = false; };
  }, [isAdmin, vals.org, misc.traffic_curve]);

  const handleSubmit = (ev) => {
    ev.preventDefault();
    if (disableSubmit) {
      return false;
    }

    if (isOverflowJob) {
      setSaving(true);
      callApi(`/jobs/${jobId}`, 'PUT', {daily_imps: vals.daily_imps})
        .then(() => goBack())
        .catch(() => setSaving(false));
      return false;
    }

    const errors = validate(vals, schema);
    //console.log('onsubmit:', errors);
    if (errors) {
      setFormState(draft => {
        draft.errors = errors;
        Object.keys(errors).forEach(field => draft.touched[field] = true);
      });
      return false;
    }
    setSaving(true);

    // normalize daily_imps
    const daily_imps = normalizeDefaultKey(vals.start_date, vals.end_date, vals.daily_imps);

    // normalize dayparts
    const dayparts = {};
    if (Object.keys(vals.dayparts).length > 0) {
      const rawDps = Object.fromEntries(
        Object.entries(vals.dayparts).map(([k, v]) => [k, v.map(t => t.format('HH:mm')).join('-')])
      );
      const normed = normalizeDefaultKey(vals.start_date, vals.end_date, rawDps);
      Object.keys(normed).forEach(k => dayparts[k] = normed[k].split('-'));
    }

    let dest_url, trackers;
    if (vals.is_adx) {
      dest_url = `adx:${vals.adx_tagid.trim()}`;
      trackers = {};
    } else {
      dest_url = vals.dest_url.trim();
      // normalize trackers
      trackers = {
        imp: vals.trackers.imp.map(s => s.trim()).filter(s => s.startsWith('http')),
        clk: vals.trackers.clk.map(s => s.trim()).filter(s => s.startsWith('http')),
      };
      trackers.imp.length > 0 || (delete trackers.imp);
      trackers.clk.length > 0 || (delete trackers.clk);
    }

    let regions = '';
    if (vals.regions !== null) {
      regions = vals.regions;
    } else if (vals.region_names !== '') {
      regions = 'names:' + vals.region_names;
    }

    const cust = JSON.parse(vals.customize || '{}');
    delete cust.device;
    delete cust.cookie;
    delete cust.bounce;
    vals.cust_device?.trim().length && (cust.device = vals.cust_device.trim());
    vals.cust_cookie?.trim().length && (cust.cookie = vals.cust_cookie.trim());
    vals.bounceTarget?.trim().length && Number.parseInt(vals.bounceTarget, 10) > 0 &&
      (cust.bounce = Number.parseInt(vals.bounceTarget, 10));

    const data = {
      ...vals,
      start_date: vals.start_date.format('YYYY-MM-DD'),
      end_date: vals.end_date.format('YYYY-MM-DD'),
      daily_imps,
      dayparts,
      dest_url,
      trackers,
      regions,
      postback: vals.postback.size === Object.keys(postbackTypes).length ? 'all' : Array.from(vals.postback).join(','),
      ant_cfg: vals.ant_cfg === CUST_MARK ? vals.ant_cfg_cust : vals.ant_cfg,
      // not saving \n for psql display and easy backup of job table
      extra: vals.extra.replaceAll('\n', '@@'),
      jump2: toJobJump2StorageValue(vals.jump2),
      customize: Object.keys(cust).length > 0 ? JSON.stringify(cust) : '',
    };
    if (vals.cross_day_schedule) {
      data.cross_day_schedule = true;
      data.cross_day_overflow_percent = Number(vals.overrun);
      data.cross_day_display_dayparts = vals.cross_day_display_dayparts || dayparts.default?.join('-');
    }
    delete data.is_adx;
    delete data.adx_tagid;
    delete data.ant_cfg_cust;
    delete data.bounceTarget;

    let url, method;
    if (jobId) {
      url = `/jobs/${jobId}`;
      method = 'PUT';
    } else {
      url = '/jobs';
      method = 'POST';
    }
    callApi(url, method, data)
      //.then(console.log)
      .then((res) => {
        if (res.errors) {
          setFormState(draft => {
            draft.errors = res.errors;
            Object.keys(res.errors).forEach(field => draft.touched[field] = true);
          });
          setSaving(false);
        } else {
          goBack();
        }
      }).catch(() => setSaving(false));
  };

  const hasError = (field) => Boolean(touched[field] && errs?.[field]);

  const handleChangeTrackers = useCallback((ev) => {
    const t = ev.target;
    let [type, i] = t.name.split(':');
    setFormState(draft => { draft.values.trackers[type][i|0] = t.value });
  }, [setFormState]);
  const addTracker = (type) => {
    setFormState(draft => { draft.values.trackers[type].push('') });
  };

  return (
    <Page styles={{padding: 3}} title={isEditing ? '修改任务' : '新建任务'}>
      <Grid container spacing={4}>
        <Grid item md={6} xs={12}>
          <Card>
            <form>
              <CardHeader title={isEditing ? `修改任务 ${jobId}: ${vals.name}` : "创建任务"} />
              <Divider />
              <CardContent>
                {isOverflowJob && (
                  <Alert severity="info" sx={{mb: 2}}>
                    这是跨日排期的溢出子任务，此页仅可修改数量。
                  </Alert>
                )}
                {!isOverflowJob && (
                <Grid container spacing={1} wrap="wrap" sx={{ '& .MuiTypography-subtitle1': {mb: 0} }}>
                {isAdmin && (
                  <Grid item container xs={12}>
                    <Grid item xs={4} md={3}>
                      <FormLabel gutterBottom variant="subtitle1">组织:</FormLabel>
                    </Grid>
                    <Grid item xs>
                      <Select
                        name="org"
                        value={vals.org}
                        size="small"
                        onChange={handleChange}
                      >
                      {Object.entries(misc['org']).map(([val, display]) => (
                        <MenuItem value={val|0} key={val}>{display}</MenuItem>
                      ))}
                      </Select>
                    </Grid>
                  </Grid>
                )}
                  <Grid item container xs={12}>
                    <Grid item xs={4} md={3}>
                      <FormLabel gutterBottom variant="subtitle1">任务名称:</FormLabel>
                    </Grid>
                    <Grid item xs>
                      <OutlinedInput
                        sx={{padding: 0, width: 400}}
                        name="name"
                        value={vals.name}
                        error={hasError("name")}
                        size="small"
                        inputProps={{ style: {textAlign: 'left'} }}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        required
                      />
                    </Grid>
                  </Grid>
                  <Grid item container xs={12}>
                    <Grid item xs={4} md={3}>
                      <FormLabel gutterBottom variant="subtitle1">广告位:</FormLabel>
                    </Grid>
                    <Grid item xs>
                      <OutlinedInput
                        sx={{padding: 0, width: 400}}
                        name="ad_slot_name"
                        value={vals.ad_slot_name}
                        error={hasError("ad_slot_name")}
                        size="small"
                        inputProps={{ style: {textAlign: 'left'} }}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Grid>
                  </Grid>
                  <Grid item container xs={12}>
                    <Grid item xs={4} md={3}>
                      <FormLabel gutterBottom variant="subtitle1">开始 / 结束:</FormLabel>
                    </Grid>
                    <Grid item xs>
                      <DatePicker
                        sx={{ width: 150, '& .MuiInputBase-input': {py: '8.5px', pl: '12px'} }}
                        value={vals.start_date}
                        views={['year', 'month', 'day']}
                        slotProps={{
                          textField: { error: hasError("start_date") }
                        }}
                        onChange={val => changeDate('start_date', val)}
                        dayOfWeekFormatter={(_, date) => dayjs(date).day() || 7}
                      />
                      <span style={{margin: '0 6px', height: '100%', display: 'inline-flex', alignItems: 'center' }}>~</span>
                      <DatePicker
                        sx={{ width: 150, '& .MuiInputBase-input': {py: '8.5px'} }}
                        value={vals.end_date}
                        views={['year', 'month', 'day']}
                        minDate={vals.start_date}
                        slotProps={{
                          textField: { error: hasError("end_date") }
                        }}
                        onChange={val => changeDate('end_date', val)}
                        dayOfWeekFormatter={(_, date) => dayjs(date).day() || 7}
                      />
                    </Grid>
                  </Grid>
                {showDayparts && (
                  <Grid item container xs={12}>
                    <Grid item xs={4} md={3}>
                      <FormLabel gutterBottom variant="subtitle1">投放时段:</FormLabel>
                    </Grid>
                    <Grid item xs sx={{display: "flex", alignItems: "center"}}>
                      {!vals.dayparts.default ? (<>
                        <Typography>全天</Typography>
                        <IconButton onClick={showEditDayparts} size="small">
                          <TuneIcon />
                        </IconButton>
                      </>) : (<>
                        <TimeField
                          sx={{
                            '& .MuiInputBase-root': {width: '55px'},
                            '& .MuiInputBase-input': {p: '5px'},
                          }}
                          format="HH:mm"
                          value={vals.dayparts.default[0]}
                          onChange={val => changeDefaultDayparts(0, val)}
                        />
                        <span style={{margin: "0 6px"}}>~</span>
                        <TimeField
                          sx={{
                            '& .MuiInputBase-root': {width: '55px'},
                            '& .MuiInputBase-input': {p: '5px'},
                          }}
                          format="HH:mm"
                          value={vals.dayparts.default[1]}
                          onChange={val => changeDefaultDayparts(1, val)}
                        />
                        <IconButton onClick={clearDayparts} size="small">
                          <ClearIcon />
                        </IconButton>
                      </>)}
                    </Grid>
                  </Grid>
                )}
                {(!isEditing || vals.cross_day_schedule) && (
                  <Grid item container xs={12}>
                    <Grid item xs={4} md={3}>
                      <FormLabel gutterBottom variant="subtitle1">跨日排期:</FormLabel>
                    </Grid>
                    <Grid item xs>
                      <FormControlLabel control={
                        <Checkbox
                          name="cross_day_schedule"
                          checked={Boolean(vals.cross_day_schedule)}
                          onChange={handleChange}
                          disabled={isEditing}
                        />
                      } label={vals.cross_day_display_dayparts || "09:00-09:00 / 10:00-10:00"} />
                    </Grid>
                  </Grid>
                )}
                  <Grid item container xs={12}>
                    <Grid item xs={4} md={3}>
                      <FormLabel gutterBottom variant="subtitle1">CTR(%):</FormLabel>
                    </Grid>
                    <Grid item xs>
                      <FormControl>
                        <OutlinedInput
                          sx={{padding: 0, width: 140}}
                          name="ctr"
                          value={vals.ctr}
                          error={hasError("ctr")}
                          size="small"
                          inputProps={{ style: {textAlign: 'right'} }}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          required
                        />
                        <FormHelperText sx={{marginLeft: 0}}>
                          {errs?.ctr ? (
                            <Box component="span" sx={{color: 'error.main'}}>{errs.ctr}.</Box>
                          ) : (
                            <span><em>100%</em> 表示全部为点击。</span>
                          )}
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                  </Grid>
                {isAdmin && (
                  <Grid item container xs={12}>
                    <Grid item xs={4} md={3}>
                      <FormLabel gutterBottom variant="subtitle1">目标二跳(%):</FormLabel>
                    </Grid>
                    <Grid item xs>
                      <FormControl>
                        <OutlinedInput
                          sx={{padding: 0, width: 140}}
                          name="jump2"
                          value={vals.jump2}
                          error={hasError("jump2")}
                          size="small"
                          inputProps={{ style: {textAlign: 'right'} }}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        <FormHelperText sx={{marginLeft: 0}}>
                          {errs?.jump2 && (
                            <Box component="span" sx={{color: 'error.main'}}>{errs.jump2}.</Box>
                          )}
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                  </Grid>
                )}
                {isAdmin && (
                  <Grid item container xs={12}>
                    <Grid item xs={4} md={3}>
                      <FormLabel gutterBottom variant="subtitle1">目标跳出率(%):</FormLabel>
                    </Grid>
                    <Grid item xs>
                      <FormControl>
                        <OutlinedInput
                          sx={{padding: 0, width: 140}}
                          name="bounceTarget"
                          value={vals.bounceTarget}
                          error={hasError("bounceTarget")}
                          size="small"
                          inputProps={{ style: {textAlign: 'right'} }}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        <FormHelperText sx={{marginLeft: 0}}>
                          {errs?.bounceTarget ? (
                            <Box component="span" sx={{color: 'error.main'}}>{errs.bounceTarget}.</Box>
                          ) : (
                            <span>仅用于监测报警；留空或 0 不检查。</span>
                          )}
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                  </Grid>
                )}
                {!isEditing && (
                  <Grid item container xs={12}>
                    <Grid item xs={4} md={3}>
                      <FormLabel gutterBottom variant="subtitle1">日流量:</FormLabel>
                    </Grid>
                    <Grid item xs>
                      <FormControl>
                        <OutlinedInput
                          sx={{padding: 0, width: 140}}
                          value={vals.daily_imps.default}
                          error={hasError("daily_imps.default")}
                          size="small"
                          inputProps={{ style: {textAlign: 'right'} }}
                          onChange={ev => changeSchedule('daily_imps', 'default', ev.target.value)}
                          onBlur={handleBlur}
                          required
                        />
                        <FormHelperText sx={{marginLeft: 0}}>
                          {errs?.['daily_imps.default'] ? (
                            <Box component="span" sx={{color: 'error.main'}}>{errs['daily_imps.default']}.</Box>
                          ) : (
                            <span>修改默认值后请检查所有日期。</span>
                          )}
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                  </Grid>
                )}
                  <Grid item container xs={12}>
                    <Grid item xs={4} md={3}>
                      <FormLabel gutterBottom variant="subtitle1">溢出比例(%):</FormLabel>
                    </Grid>
                    <Grid item xs>
                      <FormControl>
                        <Stack direction="row" spacing={2}>
                          <OutlinedInput
                            sx={{padding: 0, width: 140}}
                            name="overrun"
                            value={vals.overrun}
                            error={hasError("overrun")}
                            size="small"
                            inputProps={{ style: {textAlign: 'right'} }}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required
                          />
                          <FormControlLabel control={
                            <Checkbox
                              name={"attr_"+jobAttrFixedOverrun}
                              checked={(vals.attrs&jobAttrFixedOverrun) > 0}
                              onChange={handleChange}
                            />
                          } label="固定" />
                        </Stack>
                        <FormHelperText sx={{marginLeft: 0}}>
                          {errs?.overrun ? (
                            <Box component="span" sx={{color: 'error.main'}}>{errs.overrun}.</Box>
                          ) : (
                            <span>溢出比例基于每日计划数计算。</span>
                          )}
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Grid item container xs={12}>
                    <Grid item xs={4} md={3}>
                      <FormLabel gutterBottom variant="subtitle1">Landing率(%):</FormLabel>
                    </Grid>
                    <Grid item xs>
                      <FormControl>
                        <OutlinedInput
                          sx={{padding: 0, width: 140}}
                          name="landing"
                          value={vals.landing}
                          error={hasError("landing")}
                          size="small"
                          inputProps={{ style: {textAlign: 'right'} }}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          required
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Grid item container xs={12}>
                    <Grid item xs={4} md={3}>
                      <FormLabel gutterBottom variant="subtitle1">流量曲线:</FormLabel>
                    </Grid>
                    <Grid item xs>
                      <Select
                        name="traffic_curve"
                        value={vals.traffic_curve}
                        size="small"
                        onChange={handleChange}
                      >
                      {Object.entries(trafficCurves).map(([val, display]) => (
                        <MenuItem value={val} key={val}>{display}</MenuItem>
                      ))}
                      </Select>
                    </Grid>
                  </Grid>
                {isAdmin && (
                  <Grid item container xs={12}>
                    <Grid item xs={4} md={3}>
                      <FormLabel gutterBottom variant="subtitle1">设备号回传:</FormLabel>
                    </Grid>
                    <Grid item xs>
                      <Switch
                        name="postback"
                        checked={vals.postback.size > 0}
                        onChange={handleChange}
                      />
                    {vals.postback.size > 0 && (
                      <FormGroup sx={{ display: 'flex', flexDirection: 'row', ml: 1 }}>
                      {Object.entries(postbackTypes).map(([val, display]) => (
                        <FormControlLabel control={
                          <Checkbox name={"pb_type_"+val} checked={vals.postback.has(val)} onChange={handleChange} />
                        } label={display} key={val} />
                      ))}
                      </FormGroup>
                    )}
                    </Grid>
                  </Grid>
                )}
                  <Grid item container xs={12}>
                    <Grid item xs={4} md={3}>
                      <FormLabel gutterBottom variant="subtitle1">来自 Adx:</FormLabel>
                    </Grid>
                    <Grid item xs>
                      <Switch
                        name="is_adx"
                        checked={vals.is_adx}
                        onChange={handleChange}
                      />
                    </Grid>
                  </Grid>
                {vals.is_adx ? (
                  <Grid item container xs={12}>
                    <Grid item xs={4} md={3}>
                      <FormLabel gutterBottom variant="subtitle1">Adx 标签ID:</FormLabel>
                    </Grid>
                    <Grid item xs>
                      <OutlinedInput
                        sx={{padding: 0, width: 400}}
                        name="adx_tagid"
                        value={vals.adx_tagid}
                        error={hasError("adx_tagid")}
                        size="small"
                        inputProps={{ style: {padding: '8.5px 14px'} }}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Grid>
                  </Grid>
                ) : (<>
                  <Grid item container xs={12}>
                    <Grid item xs={4} md={3}>
                      <FormLabel gutterBottom variant="subtitle1">落地页代码:</FormLabel>
                    </Grid>
                    <Grid item xs>
                      <OutlinedInput
                        sx={{padding: 0, width: 400}}
                        name="dest_url"
                        value={vals.dest_url}
                        error={hasError("dest_url") || isInvalidUrl(vals.dest_url)}
                        size="small"
                        inputProps={{ style: {padding: '8.5px 14px'} }}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        multiline
                      />
                    </Grid>
                  </Grid>
                  <Grid item container xs={12}>
                    <Grid item xs={4} md={3}>
                      <FormLabel gutterBottom variant="subtitle1">展示代码:</FormLabel>
                    </Grid>
                    <Grid item xs>
                    {vals.trackers.imp.map((tkr, i) => (
                      <OutlinedInput
                        key={i}
                        sx={{padding: 0, width: 400, mt: i > 0 ? '5px' : 0}}
                        name={`imp:${i}`}
                        value={tkr}
                        error={isInvalidUrl(tkr)}
                        size="small"
                        inputProps={{ style: {padding: '8.5px 14px'} }}
                        onChange={handleChangeTrackers}
                        onBlur={handleBlur}
                        multiline
                      />
                    ))}
                      <IconButton onClick={() => addTracker('imp')} size="small">
                        <AddIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                  <Grid item container xs={12}>
                    <Grid item xs={4} md={3}>
                      <FormLabel gutterBottom variant="subtitle1">点击代码:</FormLabel>
                    </Grid>
                    <Grid item xs>
                    {vals.trackers.clk.map((tkr, i) => (
                      <OutlinedInput
                        key={i}
                        sx={{padding: 0, width: 400, mt: i > 0 ? '5px' : 0}}
                        name={`clk:${i}`}
                        value={tkr}
                        error={isInvalidUrl(tkr)}
                        size="small"
                        inputProps={{ style: {padding: '8.5px 14px'} }}
                        onChange={handleChangeTrackers}
                        onBlur={handleBlur}
                        multiline
                      />
                    ))}
                      <IconButton onClick={() => addTracker('clk')} size="small">
                        <AddIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                  </>
                )}
                {isAdmin && (
                  <Grid item container xs={12}>
                    <Grid item xs={4} md={3}>
                      <FormLabel gutterBottom variant="subtitle1">设备比例:</FormLabel>
                    </Grid>
                    <Grid item xs>
                      <Select
                        name="device_filter"
                        value={vals.device_filter}
                        size="small"
                        onChange={handleChange}
                        displayEmpty
                      >
                      {Object.entries(misc['device_filter']).map(([val, display]) => (
                        <MenuItem value={val} key={val}>{display}</MenuItem>
                      ))}
                      </Select>
                    </Grid>
                  </Grid>
                )}
                {isAdmin && (
                  <Grid item container xs={12}>
                    <Grid item xs={4} md={3}>
                      <FormLabel gutterBottom variant="subtitle1">页面行为:</FormLabel>
                    </Grid>
                    <Grid item xs>
                      <Select
                        name="ant_cfg"
                        value={vals.ant_cfg}
                        size="small"
                        onChange={handleChange}
                        displayEmpty
                      >
                      {Object.entries(misc['ant_cfg']).map(([val, display]) => (
                        <MenuItem value={val} key={val}>{display}</MenuItem>
                      ))}
                      </Select>
                      {vals.ant_cfg === CUST_MARK && (
                      <OutlinedInput
                        sx={{ml: '8px', p: 0, width: 200}}
                        name="ant_cfg_cust"
                        value={vals.ant_cfg_cust}
                        error={hasError("ant_cfg_cust")}
                        size="small"
                        inputProps={{ style: {textAlign: 'left'} }}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        required
                      />
                      )}
                    </Grid>
                  </Grid>
                )}
                  <Grid item container xs={12}>
                    <Grid item xs={4} md={3}>
                      <FormLabel gutterBottom variant="subtitle1">频控ID:</FormLabel>
                    </Grid>
                    <Grid item xs>
                      <FormControl>
                        <OutlinedInput
                          sx={{padding: 0, width: 140}}
                          name="fc_id"
                          value={vals.fc_id}
                          error={hasError("fc_id")}
                          size="small"
                          inputProps={{ style: {textAlign: 'right'} }}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Grid item container xs={12}>
                    <Grid item xs={4} md={3}>
                      <FormLabel gutterBottom variant="subtitle1">最大频控:</FormLabel>
                    </Grid>
                    <Grid item xs>
                      <Select
                        name="fc_max"
                        value={vals.fc_max}
                        size="small"
                        onChange={handleChange}
                        displayEmpty
                      >
                      {[1,2,3,4,5,6,7,8].map((n) => (
                        <MenuItem value={n} key={n}>{n}</MenuItem>
                      ))}
                      </Select>
                    </Grid>
                  </Grid>
                  <Grid item container xs={12}>
                    <Grid item xs={4} md={3}>
                      <FormLabel gutterBottom variant="subtitle1">订单ID:</FormLabel>
                    </Grid>
                    <Grid item xs>
                      <FormControl>
                        <OutlinedInput
                          sx={{padding: 0, width: 140}}
                          name="parent"
                          value={vals.parent}
                          error={hasError("parent")}
                          size="small"
                          inputProps={{ style: {textAlign: 'right'} }}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Grid item container xs={12}>
                    <Grid item xs={4} md={3}>
                      <FormLabel gutterBottom variant="subtitle1">地域:</FormLabel>
                    </Grid>
                    <Grid item xs>
                      <FormControl>
                        <OutlinedInput
                          sx={{padding: 0, width: 400}}
                          name="region_names"
                          value={vals.region_names}
                          error={hasError("region_names") || hasError("regions")}
                          size="small"
                          inputProps={{ style: {padding: '8.5px 14px'} }}
                          onChange={handleChange}
                          onBlur={handleValidateRegions}
                          multiline
                          required
                        />
                        <FormHelperText sx={{marginLeft: 0}}>
                          {errs?.regions ? (
                            <Box component="span" sx={{color: 'error.main'}}>{errs.regions}.</Box>
                          ) : (
                            <span>留空表示全国。</span>
                          )}
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Grid item container xs={12}>
                    <Grid item xs={4} md={3}>
                      <FormLabel gutterBottom variant="subtitle1">属性:</FormLabel>
                    </Grid>
                    <Grid item xs>
                      <FormGroup sx={{ display: 'flex', flexDirection: 'row', ml: 1 }}>
                      {jobAttrs.filter(a => isAdmin || a.val === 4).map(a => (
                        <FormControlLabel control={
                          <Checkbox name={"attr_"+a.val} checked={(vals.attrs&a.val) > 0} onChange={handleChange} />
                        } label={a.display} key={a.val} />
                      ))}
                      </FormGroup>
                    </Grid>
                  </Grid>
                  <Grid item container xs={12}>
                    <Grid item xs={4} md={3}>
                      <FormLabel gutterBottom variant="subtitle1">其他信息:</FormLabel>
                    </Grid>
                    <Grid item xs>
                      <OutlinedInput
                        sx={{padding: 0, width: 400}}
                        name="extra"
                        value={vals.extra}
                        error={hasError("extra")}
                        size="small"
                        inputProps={{ style: {padding: '8.5px 14px'} }}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        multiline
                      />
                    </Grid>
                  </Grid>
                {isAdmin && (
                  <Grid item container xs={12}>
                    <Grid item xs={4} md={3}>
                      <FormLabel gutterBottom variant="subtitle1">接受Cookies:</FormLabel>
                    </Grid>
                    <Grid item xs>
                      <OutlinedInput
                        sx={{padding: 0, width: 140}}
                        name="cust_cookie"
                        value={vals.cust_cookie}
                        error={hasError("cust_cookie")}
                        size="small"
                        inputProps={{ style: {textAlign: 'right'} }}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Grid>
                  </Grid>
                )}
                {isAdmin && (
                  <Grid item container xs={12}>
                    <Grid item xs={4} md={3}>
                      <FormLabel gutterBottom variant="subtitle1">设备展示:</FormLabel>
                    </Grid>
                    <Grid item xs>
                      <OutlinedInput
                        sx={{padding: 0, width: 400}}
                        name="cust_device"
                        value={vals.cust_device}
                        error={hasError("cust_device")}
                        size="small"
                        inputProps={{ style: {padding: '8.5px 14px'} }}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Grid>
                  </Grid>
                )}
                </Grid>
                )}
              </CardContent>
              <Divider />
              <CardActions>
                <Button
                  color="primary"
                  variant="outlined"
                  onClick={handleSubmit}
                  disabled={disableSubmit}
                >
                  {saving ? "保存中..." : "保存"}
                </Button>
                <Button
                  color="primary"
                  variant="outlined"
                  onClick={goBack}
                >
                  取消
                </Button>
              </CardActions>
            </form>
          </Card>
        </Grid>

        <Grid item md={6} xs={12}>
          {(!vals.start_date || !vals.end_date) ? (
          <Alert severity="error">请选择开始日期和结束日期！</Alert>
          ) : (
          <ScheduleEditor
            onChange={changeSchedule}
            startDate={vals.start_date}
            endDate={vals.end_date}
            dailyImps={vals.daily_imps}
            dayparts={isOverflowJob ? {} : vals.dayparts}
          />
          )}
        </Grid>
      </Grid>
    </Page>
  );
};

export default JobForm;
